import { useQuery } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export type ScheduleScope = "tech" | "sales";
export type CapacityBucket = "RED" | "AMBER" | "GREEN" | "DARK_GREEN" | "WARNING";

export interface ScheduleWeek {
  iso_week: string;
  week_start: string;
  week_end: string;
  display_label: string;
  display_label_long: string;
  fill_pct: number;
  bucket: CapacityBucket;
  days_worked?: number;
  jobs_count?: number;
  minutes_scheduled?: number;
  hours_scheduled?: number;
  appointments_count?: number;
}

export interface ScheduleRoute {
  route_id: number;
  route_code: string;
  route_name: string;
  route_label: string;
  vonigo_route_id: number;
  collaborator_name: string;
  weeks: ScheduleWeek[];
}

export interface ScheduleCapacity {
  scope: ScheduleScope;
  weekly_target: {
    value: number;
    full_pct: number;
  };
  horizon?: {
    past_days: number;
    future_days: number;
    future_weeks: number;
  };
  routes: ScheduleRoute[];
  empty_reason?: "no_classified_routes" | "schedule_sync_pending";
  empty_cta?: string;
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

export function useScheduleCapacity(scope: ScheduleScope) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["schedule-capacity", scope],
    queryFn: () => fetchJson<ScheduleCapacity>(
      `/api/cockpit/schedule/capacity?scope=${scope}`,
      { headers },
    ),
    staleTime: 5 * 60 * 1000,
  });
}
